import { z } from "zod";

import { type Database } from "@fitness-app/supabase";

import { funnelSectionTypes } from "../landing-templates/sectionTypes";

export type LandingPageEntity = Database["public"]["Tables"]["landing_page"]["Row"];

export enum LandingPageStatus {
  Draft = "draft",
  Published = "published",
  Archived = "archived",
}

/**
 * What a builder page is: a marketing landing, or a funnel step template (TRE-2356).
 * Funnel templates are rendered on `/oferta/<funnel>/<step>`, never on their own slug.
 */
export enum LandingPageKind {
  Landing = "landing",
  Checkout = "checkout",
  Upsell = "upsell",
  Downsell = "downsell",
  ThankYou = "thank_you",
}

export const landingPageKindLabels: Record<LandingPageKind, string> = {
  [LandingPageKind.Landing]: "Landing",
  [LandingPageKind.Checkout]: "Checkout",
  [LandingPageKind.Upsell]: "Upsell",
  [LandingPageKind.Downsell]: "Downsell",
  [LandingPageKind.ThankYou]: "Podziękowanie",
};

export enum LandingSignupFlowActionType {
  GoToPackagesPage = "goToPackagesPage",
  GoToPackagesHash = "goToPackagesHash",
  GoToCheckoutVariant = "goToCheckoutVariant",
  GoToBeforeConsultation = "goToBeforeConsultation",
}

export type LandingSignupFlowAction =
  | { type: LandingSignupFlowActionType.GoToPackagesPage }
  | { type: LandingSignupFlowActionType.GoToPackagesHash }
  | {
      type: LandingSignupFlowActionType.GoToCheckoutVariant;
      variantSlug?: string | null;
    }
  | { type: LandingSignupFlowActionType.GoToBeforeConsultation };

export interface LandingQuestionOption {
  id: string;
  label: string;
  value: string;
  description?: string;
}

export interface LandingQuestion {
  id: string;
  title: string;
  description?: string;
  options: LandingQuestionOption[];
  selectionMode?: "single" | "multi";
  continueButtonLabel?: string;
  autoAdvanceOnSelect?: boolean;
  progressLabel?: string;
  hasDownsellTrigger?: boolean;
  downsellTriggerOptionIds?: string[];
}

export type LandingFlowConfig = Record<string, unknown>;
export type LandingPageVisualTheme = "pink" | "gold";
export type LandingPageForcedTheme = "system" | "light" | "dark";

export interface LandingSignupFlowConfig {
  eventTypeUri: string;
  questions: LandingQuestion[];
  flowConfig?: LandingFlowConfig;
  colorTheme?: "gold" | "pink";
  storageKeyPrefix: string;
  disableCache?: boolean;
  flow?: "booking" | "checkout";
  /** Overrides the default downsell destination (e.g. the Hollywood Reset promo funnel). */
  bikiniChallengeUrl?: string;
  qualifiedAction: LandingSignupFlowAction;
  packageSelectAction: LandingSignupFlowAction;
  bookingCompleteAction: LandingSignupFlowAction;
}

/**
 * v2 signup flow — a data-driven graph (opt-in via `signupFlowV2`). Unlike the
 * v1 `signupFlow` (which fills content into a fixed step machine), the graph lets
 * a landing compose an arbitrary sequence of steps and branch on answers. It is
 * purely additive: landings without `signupFlowV2` keep using v1 unchanged.
 */
export interface LandingPackageCard {
  id: string;
  emoji: string;
  emojiGradient: string;
  priceChipGradient: string;
  name: string;
  priceAmount: string;
  priceCurrency?: string;
  priceInterval?: string;
  description: string;
  features: string[];
  ctaLabel: string;
  footerNote?: string;
}

export interface LandingPackagesConfig {
  title?: string;
  description?: string;
  packages: LandingPackageCard[];
  learnMoreCard?: { emoji?: string; title?: string; description?: string; ctaLabel?: string };
}

export type SignupFlowNodeType =
  | "intro"
  | "question"
  | "userData"
  | "consultation"
  | "calendar"
  | "packages"
  | "downsellChoice"
  | "downsell"
  | "redirect";

export type SignupFlowNode =
  | { id: string; type: "intro"; props: { title?: string; description?: string; ctaLabel?: string } }
  | { id: string; type: "question"; props: LandingQuestion }
  | {
      id: string;
      type: "userData";
      props: { title?: string; description?: string; showPhone?: boolean; requirePhone?: boolean; ctaLabel?: string };
    }
  | { id: string; type: "consultation"; props: { title?: string; ctaLabel?: string; hideDetails?: boolean } }
  | { id: string; type: "calendar"; props: { eventTypeUri?: string } }
  | { id: string; type: "packages"; props: LandingPackagesConfig }
  | { id: string; type: "downsellChoice"; props: { hideSpecialistOption?: boolean } }
  | {
      id: string;
      type: "downsell";
      props: { title?: string; paragraphs?: string[]; ctaLabel?: string; bikiniChallengeUrl?: string };
    }
  | { id: string; type: "redirect"; props: { action: LandingSignupFlowAction } };

export interface SignupFlowEdge {
  from: string;
  to: string;
  /** Named outlet a node fires (e.g. "specialist"/"simplePlan" for downsellChoice, "booked" for calendar). Absent = default. */
  on?: string;
  /** Answer-based branch: taken only when answers[answerOf] intersects `equals`. */
  when?: { answerOf: string; equals: string[] };
}

export interface LandingSignupFlowGraph {
  entry: string;
  eventTypeUri: string;
  colorTheme?: "gold" | "pink";
  storageKeyPrefix?: string;
  disableCache?: boolean;
  flow?: "booking" | "checkout";
  nodes: SignupFlowNode[];
  edges: SignupFlowEdge[];
}

export const baseLandingSectionTypes = [
  "headerV2",
  "headerClassic",
  "hollywoodHero",
  "metamorfozaHeroVideo",
  "promoVideo",
  "formSection",
  "heroRating",
  "transformationCarousel",
  "statisticsSection",
  "didYouKnow",
  "featuresCarousel",
  "quizBanner",
  "stepsTimeline",
  "practiceCarousel",
  "metamorfozyCta",
  "hollywoodFaq",
  "ratingSection",
  "hollywoodFooter",
  "pakietyHero",
  "pakietyCards",
  "pakietyStaticSection",
  "pakietyStatistics",
  "stickyPakietyCta",
  "reviews",
  "whyUs",
  "basicFaq",
  "transformationsGrid",
  "campaignBanner",
  "campaignCountdown",
  "transformationsClassic",
  "reviewsClassic",
  "footerClassic",
] as const;

/** Marketing sections plus the funnel template sections (`landing-templates`). */
export const landingSectionTypes = [...baseLandingSectionTypes, ...funnelSectionTypes] as const;

export type LandingSectionType = (typeof landingSectionTypes)[number];

export interface LandingSectionConfig<Props = Record<string, unknown>> {
  id: string;
  type: LandingSectionType;
  enabled: boolean;
  trackingName?: string;
  props: Props;
}

export interface LandingPageConfig {
  seo: {
    title: string;
    description: string;
    ogImage?: string;
    facebookDomainVerification?: string;
    favicon?: string;
  };
  tracking: {
    pageName: string;
    facebookPixelId?: string;
    googleAnalyticsId?: string;
  };
  layout: {
    forcedTheme: LandingPageForcedTheme;
    visualTheme?: LandingPageVisualTheme;
    accentColor?: string;
    fontPreset: "hollywood";
    backgroundClassName?: string;
    /**
     * Page frame: `landing` (default) = `LandingLayout`, `embedding` = the checkout and
     * post-purchase frame (`EmbeddingLayout`), `none` = no frame (the Bikini landings).
     */
    shell?: "landing" | "embedding" | "none";
  };
  signupFlow: LandingSignupFlowConfig;
  signupFlowV2?: LandingSignupFlowGraph;
  sections: LandingSectionConfig[];
}

export interface LandingPage extends Omit<
  LandingPageEntity,
  "draftConfig" | "metadata" | "publishedConfig" | "status" | "kind"
> {
  status: LandingPageStatus;
  kind: LandingPageKind;
  draftConfig: LandingPageConfig;
  publishedConfig: LandingPageConfig | null;
  metadata: Record<string, unknown> | null;
}

const signupFlowActionSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal(LandingSignupFlowActionType.GoToPackagesPage) }),
  z.object({ type: z.literal(LandingSignupFlowActionType.GoToPackagesHash) }),
  z.object({
    type: z.literal(LandingSignupFlowActionType.GoToCheckoutVariant),
    variantSlug: z.string().nullish(),
  }),
  z.object({
    type: z.literal(LandingSignupFlowActionType.GoToBeforeConsultation),
  }),
]);

const landingQuestionSchema = z.object({
  id: z.string().min(1),
  title: z.string().min(1),
  description: z.string().optional(),
  options: z.array(
    z.object({
      id: z.string().min(1),
      label: z.string().min(1),
      value: z.string().min(1),
      description: z.string().optional(),
    }),
  ),
  selectionMode: z.enum(["single", "multi"]).optional(),
  continueButtonLabel: z.string().optional(),
  autoAdvanceOnSelect: z.boolean().optional(),
  progressLabel: z.string().optional(),
  hasDownsellTrigger: z.boolean().optional(),
  downsellTriggerOptionIds: z.array(z.string()).optional(),
}) satisfies z.ZodType<LandingQuestion>;

const landingPackageCardSchema = z.object({
  id: z.string().min(1),
  emoji: z.string(),
  emojiGradient: z.string(),
  priceChipGradient: z.string(),
  name: z.string().min(1),
  priceAmount: z.string(),
  priceCurrency: z.string().optional(),
  priceInterval: z.string().optional(),
  description: z.string(),
  features: z.array(z.string()),
  ctaLabel: z.string(),
  footerNote: z.string().optional(),
});

const landingPackagesConfigSchema = z.object({
  title: z.string().optional(),
  description: z.string().optional(),
  packages: z.array(landingPackageCardSchema),
  learnMoreCard: z
    .object({
      emoji: z.string().optional(),
      title: z.string().optional(),
      description: z.string().optional(),
      ctaLabel: z.string().optional(),
    })
    .optional(),
});

const signupFlowNodeSchema = z.discriminatedUnion("type", [
  z.object({
    id: z.string().min(1),
    type: z.literal("intro"),
    props: z
      .object({ title: z.string().optional(), description: z.string().optional(), ctaLabel: z.string().optional() })
      .default({}),
  }),
  z.object({ id: z.string().min(1), type: z.literal("question"), props: landingQuestionSchema }),
  z.object({
    id: z.string().min(1),
    type: z.literal("userData"),
    props: z
      .object({
        title: z.string().optional(),
        description: z.string().optional(),
        showPhone: z.boolean().optional(),
        requirePhone: z.boolean().optional(),
        ctaLabel: z.string().optional(),
      })
      .default({}),
  }),
  z.object({
    id: z.string().min(1),
    type: z.literal("consultation"),
    props: z
      .object({ title: z.string().optional(), ctaLabel: z.string().optional(), hideDetails: z.boolean().optional() })
      .default({}),
  }),
  z.object({
    id: z.string().min(1),
    type: z.literal("calendar"),
    props: z.object({ eventTypeUri: z.string().optional() }).default({}),
  }),
  z.object({ id: z.string().min(1), type: z.literal("packages"), props: landingPackagesConfigSchema }),
  z.object({
    id: z.string().min(1),
    type: z.literal("downsellChoice"),
    props: z.object({ hideSpecialistOption: z.boolean().optional() }).default({}),
  }),
  z.object({
    id: z.string().min(1),
    type: z.literal("downsell"),
    props: z
      .object({
        title: z.string().optional(),
        paragraphs: z.array(z.string()).optional(),
        ctaLabel: z.string().optional(),
        bikiniChallengeUrl: z.string().optional(),
      })
      .default({}),
  }),
  z.object({ id: z.string().min(1), type: z.literal("redirect"), props: z.object({ action: signupFlowActionSchema }) }),
]);

const signupFlowEdgeSchema = z.object({
  from: z.string().min(1),
  to: z.string().min(1),
  on: z.string().optional(),
  when: z.object({ answerOf: z.string().min(1), equals: z.array(z.string()) }).optional(),
});

export const landingSignupFlowGraphSchema = z.object({
  entry: z.string().min(1),
  eventTypeUri: z.string().min(1),
  colorTheme: z.enum(["gold", "pink"]).optional(),
  storageKeyPrefix: z.string().optional(),
  disableCache: z.boolean().optional(),
  flow: z.enum(["booking", "checkout"]).optional(),
  nodes: z.array(signupFlowNodeSchema),
  edges: z.array(signupFlowEdgeSchema),
});

export const landingPageConfigSchema = z.object({
  seo: z.object({
    title: z.string().min(1),
    description: z.string().min(1),
    ogImage: z.string().optional(),
    facebookDomainVerification: z.string().optional(),
    favicon: z.string().optional(),
  }),
  tracking: z.object({
    pageName: z.string().min(1),
    facebookPixelId: z.string().optional(),
    googleAnalyticsId: z.string().optional(),
  }),
  layout: z.object({
    forcedTheme: z.enum(["system", "light", "dark"]),
    visualTheme: z.enum(["pink", "gold"]).optional(),
    accentColor: z.string().optional(),
    fontPreset: z.literal("hollywood"),
    backgroundClassName: z.string().optional(),
    shell: z.enum(["landing", "embedding", "none"]).optional(),
  }),
  signupFlow: z.object({
    eventTypeUri: z.string().min(1),
    questions: z.array(landingQuestionSchema).default([]),
    flowConfig: z.record(z.string(), z.unknown()).optional(),
    colorTheme: z.enum(["gold", "pink"]).optional(),
    storageKeyPrefix: z.string().min(1),
    disableCache: z.boolean().optional(),
    flow: z.enum(["booking", "checkout"]).optional(),
    bikiniChallengeUrl: z.string().optional(),
    qualifiedAction: signupFlowActionSchema,
    packageSelectAction: signupFlowActionSchema,
    bookingCompleteAction: signupFlowActionSchema,
  }),
  signupFlowV2: landingSignupFlowGraphSchema.optional(),
  sections: z.array(
    z.object({
      id: z.string().min(1),
      type: z.enum(landingSectionTypes),
      enabled: z.boolean(),
      trackingName: z.string().optional(),
      props: z.record(z.string(), z.unknown()).default({}),
    }),
  ),
});

export const parseLandingPageConfig = (config: unknown): LandingPageConfig =>
  landingPageConfigSchema.parse(config) as LandingPageConfig;

export const parseSignupFlowGraph = (graph: unknown): LandingSignupFlowGraph =>
  landingSignupFlowGraphSchema.parse(graph) as LandingSignupFlowGraph;

/** Resolves the next node id for a fired outlet, honoring answer-based `when` conditions. */
export const resolveSignupFlowNext = (
  graph: LandingSignupFlowGraph,
  fromNodeId: string,
  outlet: string | undefined,
  answers: Record<string, string | string[]>,
): string | null => {
  const answerMatches = (edge: SignupFlowEdge): boolean => {
    if (!edge.when) {
      return true;
    }
    const answer = answers[edge.when.answerOf];
    const values = Array.isArray(answer) ? answer : answer ? [answer] : [];
    return values.some((value) => edge.when?.equals.includes(value));
  };

  const candidates = graph.edges.filter(
    (edge) => edge.from === fromNodeId && (edge.on ?? "default") === (outlet ?? "default"),
  );
  // Conditional edges win over the unconditional default, so evaluate them first.
  const conditional = candidates.find((edge) => edge.when && answerMatches(edge));
  if (conditional) {
    return conditional.to;
  }
  const fallback = candidates.find((edge) => !edge.when);
  return fallback ? fallback.to : null;
};

export const SIGNUP_FLOW_NODE_TYPES: SignupFlowNodeType[] = [
  "intro",
  "question",
  "userData",
  "consultation",
  "calendar",
  "packages",
  "downsellChoice",
  "downsell",
  "redirect",
];

export const signupFlowNodeTypeLabel = (type: SignupFlowNodeType): string =>
  ({
    intro: "Intro",
    question: "Pytanie",
    userData: "Dane kontaktowe",
    consultation: "Konsultacja",
    calendar: "Kalendarz (Calendly)",
    packages: "Pakiety",
    downsellChoice: "Wybór (downsell)",
    downsell: "Downsell",
    redirect: "Przekierowanie",
  })[type];

/** Builds a sensible default node of the given type — shared by both landing editors. */
export const createSignupFlowNode = (type: SignupFlowNodeType, id: string): SignupFlowNode => {
  switch (type) {
    case "intro":
      return { id, type, props: { title: "Zacznijmy!", description: "", ctaLabel: "Zaczynam" } };
    case "question":
      return {
        id,
        type,
        props: {
          id,
          title: "Nowe pytanie",
          selectionMode: "single",
          autoAdvanceOnSelect: true,
          options: [{ id: "opcja-1", label: "Pierwsza odpowiedź", value: "opcja-1" }],
        },
      };
    case "userData":
      return { id, type, props: { title: "Zostaw swoje dane", showPhone: false, ctaLabel: "Dalej" } };
    case "consultation":
      return { id, type, props: { title: "Umów konsultację", ctaLabel: "Wybierz termin" } };
    case "calendar":
      return { id, type, props: {} };
    case "packages":
      return { id, type, props: { packages: [] } };
    case "downsellChoice":
      return { id, type, props: {} };
    case "downsell":
      return { id, type, props: { title: "Prostszy start", paragraphs: [], ctaLabel: "Dołącz do wyzwania" } };
    case "redirect":
      return { id, type, props: { action: { type: LandingSignupFlowActionType.GoToBeforeConsultation } } };
    default:
      return { id, type: "intro", props: {} };
  }
};

/** Starter branching booking graph used when enabling v2 from either editor. */
export const createStarterSignupFlowGraph = (
  eventTypeUri: string,
  colorTheme: "gold" | "pink" = "gold",
): LandingSignupFlowGraph => ({
  entry: "intro",
  eventTypeUri,
  colorTheme,
  flow: "booking",
  nodes: [
    createSignupFlowNode("intro", "intro"),
    createSignupFlowNode("question", "q1"),
    createSignupFlowNode("userData", "userData"),
    createSignupFlowNode("consultation", "consultation"),
    createSignupFlowNode("calendar", "calendar"),
    createSignupFlowNode("redirect", "booked"),
  ],
  edges: [
    { from: "intro", to: "q1" },
    { from: "q1", to: "userData" },
    { from: "userData", to: "consultation" },
    { from: "consultation", to: "calendar" },
    { from: "calendar", to: "booked", on: "booked" },
  ],
});

/**
 * Sanitizes admin-authored rich text before it is injected with
 * `dangerouslySetInnerHTML`. The whole string is HTML-escaped first, then a
 * fixed allow-list of simple formatting tags is restored verbatim. Because only
 * exact tokens (e.g. `<strong>`) are un-escaped, no attribute — and therefore no
 * `onerror`/`onclick`/`href="javascript:"` — can survive, which closes the
 * stored-XSS vector while preserving the `<br />`/`<strong>` formatting the copy
 * relies on. Idempotent: sanitizing an already-sanitized string is a no-op.
 */
export const sanitizeLandingRichText = (value: string): string => {
  const escaped = value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

  const allowedTags: Array<[RegExp, string]> = [
    [/&lt;br\s*\/?&gt;/gi, "<br />"],
    [/&lt;(\/?)(strong|b|em|i|u|p|span|s|mark|small)&gt;/gi, "<$1$2>"],
  ];

  return allowedTags.reduce((acc, [pattern, replacement]) => acc.replace(pattern, replacement), escaped);
};

export const getLandingVisualTheme = (config: LandingPageConfig): LandingPageVisualTheme =>
  config.layout.visualTheme ?? config.signupFlow.colorTheme ?? "pink";

export const getLandingPath = (slug: string): string => {
  const normalizedSlug = slug.replace(/^\/+|\/+$/g, "");
  return normalizedSlug ? `/${normalizedSlug}` : "/";
};
